/**
 * Profile and AI-preference endpoints for the signed-in account.
 *
 * Two contract details shape this module:
 *
 * - `PUT /users/me` treats `null` as "leave unchanged", so a partial form must send **only** the fields the user
 *   actually edited. The payload types below make that explicit instead of relying on `undefined` travelling
 *   through JSON (where it silently disappears).
 * - An empty `avatarUrl` **clears** the avatar, while omitting it keeps the current one.
 *
 * There is no credential endpoint here: since ADR-0019 every call runs on an administrator-managed platform provider
 * profile, so the account stores no provider secret of its own.
 */

import { apiClient } from '@/lib/apiClient';
import type {
  AiProviderKind,
  AiProviderOption,
  PromptLevel,
  ThemePreference,
  UserProfile,
} from '@/types/api';

/** Body of `PUT /users/me`; every member is optional and `null` means "unchanged". */
export interface UpdateProfilePayload {
  displayName?: string;
  avatarUrl?: string;
  locale?: string;
  theme?: ThemePreference;
}

/** Body of `PUT /users/me/ai-preferences`. */
export interface UpdateAiPreferencesPayload {
  preferredPromptLevel: PromptLevel;
  /** Provider family the wizard and the co-pilot pre-fill from, or `null` to leave the picker empty. */
  preferredProviderKind?: AiProviderKind | null;
  /** Model identifier to pre-fill, or `null` for the provider default. */
  preferredModelId?: string | null;
}

/** Profile and AI-preference operations. */
export const usersApi = {
  /** Reads the profile of the signed-in account. */
  async getProfile(): Promise<UserProfile> {
    const { data } = await apiClient.get<UserProfile>('/users/me');
    return data;
  },

  /** Applies a partial profile update and returns the new profile. */
  async updateProfile(payload: UpdateProfilePayload): Promise<UserProfile> {
    const body: Record<string, unknown> = {};

    // Only the members the user touched are serialized, so an untouched field keeps its stored value.
    if (payload.displayName !== undefined) {
      body.displayName = payload.displayName;
    }

    if (payload.avatarUrl !== undefined) {
      body.avatarUrl = payload.avatarUrl;
    }

    if (payload.locale !== undefined) {
      body.locale = payload.locale;
    }

    if (payload.theme !== undefined) {
      body.theme = payload.theme;
    }

    const { data } = await apiClient.put<UserProfile>('/users/me', body);
    return data;
  },

  /** Stores the default provider, model and prompt depth used to pre-fill the wizard. */
  async updateAiPreferences(payload: UpdateAiPreferencesPayload): Promise<UserProfile> {
    const { data } = await apiClient.put<UserProfile>('/users/me/ai-preferences', payload);
    return data;
  },
};

/** Provider catalog and co-pilot metadata. */
export const aiApi = {
  /** Reads the provider and model catalog the settings picker renders. */
  async getProviders(): Promise<AiProviderOption[]> {
    const { data } = await apiClient.get<AiProviderOption[]>('/ai/providers');
    return data;
  },
};
